import type { TaskSummary, TicketTask } from '../types';

/* Pure helpers for sub-ticket tasks, shared by the Tasks card, the task
   dialog, list marks and the portal. Mirror the server's rules so the UI
   never offers an action the API would refuse. */

export interface TaskAccess {
  myId?: string | null;
  isStaff: boolean;
  isCollaborator: boolean;
}

/** Staff, collaborators, and the task's own assignee may work a task (note, complete, reopen, reassign). */
export function canManageTask(task: { assignedToId?: string | null }, a: TaskAccess): boolean {
  return a.isStaff || a.isCollaborator || (!!a.myId && task.assignedToId === a.myId);
}

/** "2 of 5 done · waiting on Gary (1), Chris (2) · 1 unassigned" — same wording as the server. */
export function summaryText(s: TaskSummary): string {
  if (s.total === 0) return 'No tasks';
  if (s.done === s.total) return `All ${s.total} done`;
  const first = (n: string) => n.trim().split(/\s+/)[0] || n;
  const parts = [`${s.done} of ${s.total} done`];
  if (s.waitingOn.length > 0) parts.push(`waiting on ${s.waitingOn.map(w => `${first(w.name)} (${w.count})`).join(', ')}`);
  if (s.unassigned > 0) parts.push(`${s.unassigned} unassigned`);
  return parts.join(' · ');
}

/** Client-side rollup for when the server summary isn't at hand. */
export function summarizeTasks(tasks: TicketTask[]): TaskSummary {
  const by = new Map<string, { id: string; name: string; count: number }>();
  let done = 0;
  let unassigned = 0;
  for (const t of tasks) {
    if (t.status === 'completed') { done++; continue; }
    const id = t.assignedTo?.id ?? t.assignedToId;
    if (!id) { unassigned++; continue; }
    const row = by.get(id) ?? { id, name: t.assignedTo?.name ?? 'Unnamed', count: 0 };
    row.count++;
    by.set(id, row);
  }
  return {
    total: tasks.length, done, open: tasks.length - done, unassigned,
    waitingOn: [...by.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)),
  };
}
